import { useState } from "react";
import { createPortal } from "react-dom";
import { glassStyle } from "../data/utils";

type Props = {
  panelOpen: boolean;
  pagePath: string;
};

export function SuggestionsButton({ panelOpen, pagePath }: Props) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Send suggestion"
        style={{
          ...glassStyle,
          position: "fixed",
          bottom: 16,
          left: panelOpen ? 356 : 16,
          zIndex: 50,
          padding: "10px 14px",
          borderRadius: 12,
          fontSize: 13,
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: 6,
          transition: "left 0.3s ease",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background =
            "rgba(99,102,241,0.22)";
          (e.currentTarget as HTMLButtonElement).style.borderColor =
            "rgba(129,140,248,0.55)";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background =
            "rgba(255,255,255,0.10)";
          (e.currentTarget as HTMLButtonElement).style.borderColor =
            "rgba(255,255,255,0.22)";
        }}
      >
        💡 Suggestions
      </button>

      {open && (
        <SuggestionsModal
          pagePath={pagePath}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function SuggestionsModal({
  pagePath,
  onClose,
}: {
  pagePath: string;
  onClose: () => void;
}) {
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!message.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch("/api/send-suggestion", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: message.trim(),
          name: name.trim() || null,
          path: pagePath,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setSuccess(true);
      setTimeout(onClose, 1500);
    } catch (err: any) {
      setError(err?.message || "Failed to send");
    } finally {
      setSubmitting(false);
    }
  }

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.55)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          zIndex: 100,
        }}
      />

      {/* Modal */}
      <form
        className="sg-modal"
        onSubmit={submit}
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(420px, calc(100vw - 32px))",
          borderRadius: 18,
          padding: 22,
          backdropFilter: "blur(28px)",
          WebkitBackdropFilter: "blur(28px)",
          background: "rgba(8,10,18,0.78)",
          border: "1px solid rgba(255,255,255,0.10)",
          boxShadow: "0 24px 60px rgba(0,0,0,0.7)",
          color: "#fff",
          zIndex: 101,
          fontFamily: "sans-serif",
        }}
      >
        <h3 style={{ margin: "0 0 4px", fontSize: 18 }}>Send a suggestion</h3>
        <p style={{ margin: "0 0 14px", fontSize: 12, opacity: 0.7 }}>
          Anything we can do better? Let us know.
        </p>

        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          required
          placeholder="Your suggestion…"
          rows={4}
          style={{
            width: "100%",
            background: "rgba(0,0,0,0.25)",
            border: "1px solid rgba(255,255,255,0.18)",
            borderRadius: 10,
            color: "#fff",
            fontSize: 13,
            padding: 10,
            resize: "vertical",
            fontFamily: "inherit",
          }}
        />

        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name (optional)"
          style={{
            marginTop: 10,
            width: "100%",
            background: "rgba(0,0,0,0.25)",
            border: "1px solid rgba(255,255,255,0.18)",
            borderRadius: 10,
            color: "#fff",
            fontSize: 13,
            padding: "10px 12px",
            fontFamily: "inherit",
          }}
        />

        {error && (
          <div
            style={{
              marginTop: 10,
              fontSize: 12,
              color: "#fca5a5",
            }}
          >
            {error}
          </div>
        )}

        {success && (
          <div
            style={{
              marginTop: 10,
              fontSize: 13,
              color: "#86efac",
              fontWeight: 600,
            }}
          >
            ✓ Suggestion sent
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 14,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.18)",
              color: "#fff",
              borderRadius: 10,
              padding: "8px 14px",
              fontSize: 13,
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !message.trim()}
            style={{
              background:
                "linear-gradient(90deg,#6366f1,#818cf8)",
              border: "none",
              color: "#fff",
              borderRadius: 10,
              padding: "8px 16px",
              fontSize: 13,
              fontWeight: 600,
              opacity: submitting || !message.trim() ? 0.55 : 1,
            }}
          >
            {submitting ? "Sending…" : "Send"}
          </button>
        </div>
      </form>
    </>,
    document.body
  );
}
